import { useEffect, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useSession } from '@/api/session';
import type { AttentionSummary } from '@/api/types';
import { useApi } from '@/api/useApi';
import { SignOutButton } from '@/components/SignOutButton';

/**
 * A full-width green app bar holding the agency name and a six-item nav
 * (screen 01). No sidebar: the design gives the content column the full
 * 1180px, and a supervisor reading a client table needs the width.
 */
const NAV = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/clients', label: 'Clients' },
  { to: '/approvals', label: 'Approve' },
  { to: '/notes', label: 'Notes' },
  { to: '/feedback', label: 'Feedback' },
  { to: '/team', label: 'Team' },
] as const;

export function Shell({ children }: { children: ReactNode }) {
  const { policy, user } = useSession();
  const { pathname } = useLocation();
  const attention = useApi<AttentionSummary>('/agency/attention');

  // Publishing a plan or clearing a queue changes these. Refetching on
  // navigation keeps the badges honest without polling.
  const reload = attention.reload;
  useEffect(() => {
    void reload();
  }, [pathname, reload]);

  const counts = attention.data?.counts;
  const badges: Record<string, { value: number; tone?: 'gold' }> = {
    '/dashboard': { value: counts?.urgent_concerns.count ?? 0 },
    '/approvals': { value: counts?.updates_to_approve.count ?? 0, tone: 'gold' },
    '/notes': { value: counts?.notes_late.count ?? 0, tone: 'gold' },
  };

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      <header className="appbar">
        <span className="appbar__brand">{policy?.agency.name ?? 'Lumenest'}</span>
        <nav className="appbar__nav" aria-label="Console sections">
          {NAV.map((item) => {
            const badge = badges[item.to];
            return (
              <NavLink key={item.to} to={item.to} className="appbar__link">
                {item.label}
                {badge && badge.value > 0 ? (
                  <span className="appbar__count" data-tone={badge.tone}>
                    {badge.value}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
        <span className="appbar__meta">
          {attention.data
            ? `${attention.data.total_clients} clients · ${attention.data.total_caregivers} caregivers`
            : ''}
        </span>
        {/* Who you are, not just where you are. The console showed the agency
            and never the person, so a coordinator at a shared desk had no way
            to tell whose session they were in — and no way to end it. */}
        <span className="appbar__who" title={user?.email ?? undefined}>
          {user?.full_name}
        </span>
        <SignOutButton />
      </header>

      <main className="main" id="main">
        {children}
      </main>
    </div>
  );
}
